import PublicLayout from "@/components/layout/PublicLayout";
import { Link } from "react-router-dom";

/**
 * Inline markdown — renders bare URLs and [label](href) into anchors so
 * the Trust Center hub can deep-link to every other policy without us
 * needing to wire bespoke JSX per page.
 *
 * Intentionally minimal: handles [text](url) plus passes through plain
 * text. Anything more complex stays as plain text.
 */
function renderInline(text) {
    const parts = [];
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    let last = 0;
    let m;
    let idx = 0;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const [, label, href] = m;
        const isInternal = href.startsWith("/");
        if (isInternal) {
            parts.push(
                <Link
                    key={`l-${idx}`}
                    to={href}
                    className="text-bl-primary underline hover:no-underline"
                >
                    {label}
                </Link>,
            );
        } else {
            parts.push(
                <a
                    key={`a-${idx}`}
                    href={href}
                    className="text-bl-primary underline hover:no-underline"
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {label}
                </a>,
            );
        }
        last = re.lastIndex;
        idx += 1;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

const PAGES = {
    privacy: {
        title: "Privacy Policy",
        body: `
## What we collect
We respect your privacy. Buyer's Leverage collects only the information needed to provide our service: account credentials (email + name), uploaded inspection PDFs, generated reports, billing details processed via Stripe, micro-survey responses, and basic operational telemetry (page views, feature clicks, error rates) used to keep the platform reliable.

## How we use it
We use your data to generate your reports, process payments, send transactional emails about your account, improve the product, and respond to support requests. We do not sell your personal information. Inspection reports are private to your account and are not publicly indexable.

## Your CCPA rights (California residents)
California Civil Code §1798.100–§1798.140 gives you the following rights, which we honor for every user regardless of state:
  • Right to know — request a copy of every category of personal information we hold on you.
  • Right to delete — request erasure of your account, reports, uploads, transactions, and audit log.
  • Right to opt out of "sale" or "sharing" of personal information — we do not sell your data, but you can lock in the opt-out at any time via the "Do Not Sell or Share My Personal Information" link in the footer or on your account privacy page.
  • Right to portability — download a machine-readable JSON export of every record we hold on you.
  • Right to non-discrimination — exercising any of these rights will not result in price changes, denial of service, or degraded quality.

## Your GDPR rights (EU / UK residents)
Articles 15-21 of GDPR additionally grant the right to rectification, restriction of processing, and the right to object to processing. Contact support to exercise any of these rights — we respond within 30 days.

## How to exercise these rights
The fastest path is the new "Your Privacy & Data" page in your account — one click each for data export, account deletion, marketing opt-out, and cookie-consent reset. Or email support and we'll handle it manually.

## Data retention
Active accounts keep their data indefinitely so reports stay available for future reference. Deletion requests are honored within 30 days and create an audit-log entry. Backups are purged within 90 days of deletion.

## Third-party processors
We use Stripe (payments), Resend (transactional email), Cloudflare R2 (encrypted backups), Google Gemini + OpenAI (report analysis & image generation), and Metricool (social-media scheduling). Each is contractually bound to GDPR / CCPA data-processor terms.

## Children
The platform is not intended for users under 18. We do not knowingly collect data from minors.

## Updates
We may revise this policy when product or regulatory changes warrant. Material changes will be announced via a banner at sign-in and an email to active users.
        `,
    },
    terms: {
        title: "Terms of Service",
        body: `
By using Buyer's Leverage you agree to these terms. The platform is a decision-support product and is not legal advice, engineering advice, contractor bidding, appraisal advice, or a replacement for a licensed home inspection.

You are responsible for the accuracy and legality of any inspection report you upload. We grant you a non-transferable right to view your generated reports.

We may update these terms occasionally. Continued use of the platform constitutes acceptance of the current terms.
        `,
    },
    cookie: {
        title: "Cookie Policy",
        body: `
We use minimal cookies necessary for authentication and session management. Optional analytics cookies help us understand product usage and improve the experience.

You can manage cookie preferences through our consent banner. Rejecting non-essential cookies will not affect core functionality.
        `,
    },
    disclaimer: {
        title: "Disclaimer",
        body: `
Buyer's Leverage is a decision-support product. It is not legal advice, engineering advice, contractor bidding, appraisal advice, or a home inspection replacement.

Repair cost ranges shown are broad planning estimates intended for negotiation context only — not contractor bids or guaranteed pricing. Always consult a qualified professional for major concerns.

Severity indicators reflect general inspection categorization and are not certifications of structural integrity, safety, or suitability for any specific purpose.
        `,
    },
    refund: {
        title: "Refund Policy",
        body: `
Because reports are digital and generated on demand, refund eligibility is limited but fair.

We will refund a paid report if the underlying analysis fails to generate and we are unable to resolve the issue, if you were charged in duplicate, or if a clear technical defect prevented you from accessing the report.

Refunds are not typically issued after a successful report has been generated and delivered. For any concern, please contact support — we want to make this right.
        `,
    },
    accessibility: {
        title: "Accessibility Statement",
        body: `
## Our commitment
Buyer's Leverage is committed to WCAG 2.2 Level AA and ADA accessibility best practices, with Section 508 best practices applied where they map onto a SaaS product. We continuously work toward conformance and treat accessibility as a first-class product requirement — not an afterthought.

## Who we support
The platform is designed to be usable by blind users, low-vision users, color-blind users, keyboard-only users, screen-reader users, users with motor impairments, users with cognitive impairments, and anyone relying on assistive technologies.

## What we do today
  • Semantic HTML5 landmarks (header / nav / main / footer) on every page.
  • Skip-to-content link in the top-left on every page — focus the tab key once from the page top.
  • Visible focus rings (2 px brand-teal outline) on every interactive element via the global :focus-visible rule.
  • Form inputs always paired with explicit labels or aria-label; error messages linked via aria-describedby.
  • aria-live polite regions announce calculator results, upload progress, and form-error toasts so screen-reader users hear updates as they happen.
  • All meaningful images carry descriptive alt text; decorative imagery uses empty alt.
  • Color contrast at or above 4.5:1 (normal text) and 3:1 (large text) per WCAG 1.4.3, and never relies on color alone — severity icons, text labels, and shapes back up every color cue.
  • Respects the system "prefers-reduced-motion" preference — auto-rotating carousels and entrance animations pause when set.
  • Respects Windows High Contrast Mode via the "forced-colors" media query.
  • Keyboard support for every interaction, including modal dismiss via Esc and accordion / dropdown control with Enter / Space / arrows.
  • Inspection reports offered in a Web view AND a PDF view (desktop embeds the real PDF inline; mobile mirrors the same content as a printed-page-styled layout for reliable screen-reader support).
  • PDF exports are generated with reportlab in tagged mode so screen readers see proper document structure, language, and reading order.

## Where we still have work to do
  • A small handful of legacy chart visualisations rely on color alone — we are adding redundant text labels and accessible summaries.
  • Some AI-generated image alt-text is reviewed quarterly rather than on every change.
  • Third-party embeds (Stripe checkout, Metricool widgets) follow their own accessibility roadmaps.

## Testing
Our CI pipeline runs axe-core and Lighthouse accessibility audits on the marketing, account, calculator, upload, and report routes. We do not deploy pages with known critical accessibility violations.

## Need help?
If you encounter a barrier on Buyer's Leverage, email support at support@buyersleverage.com with the URL, the device + assistive technology you are using, and a brief description. We respond to accessibility issues within 5 business days and treat them as priority. You can also escalate via the contact form at /contact.

## External audit cadence
We commit to an external WCAG 2.2 AA audit every 12 months. Between audits we run continuous in-house tooling. The most recent and next scheduled audit dates are published on the Accessibility & Trust Center.

## Last updated
This statement is reviewed every 12 months or sooner when material UI changes ship.
        `,
    },
    "ai-disclosure": {
        title: "AI Disclosure",
        body: `
## How AI is used here
Buyer's Leverage uses AI-assisted analysis to read your uploaded inspection report, classify findings by severity, attach planning-range repair-cost exposures, and assemble a structured negotiation play. Specific systems involved: Google Gemini for visual and structural parsing, OpenAI models for narrative summarisation, and our own deterministic rule layer for severity scoring and cost banding.

## What this means for you
  • AI outputs may contain errors. Treat every figure, finding, and recommendation as a starting point — not a final answer.
  • Repair-cost exposures are planning ranges based on national and regional contractor data, not quotes. Real quotes vary with market, season, contractor, and scope.
  • Severity classifications follow our published rubric but a licensed inspector, engineer, or contractor may disagree on edge cases.
  • Reports are educational decision-support tools — they are NOT a replacement for a licensed home inspection, structural engineering opinion, or legal advice.

## What you should do
Always verify important information before making purchase, repair, or negotiation decisions:
  • Get a contractor or specialist quote on any high-cost finding before pricing it into your offer.
  • Re-read the inspector's actual narrative for context the AI may have flattened.
  • Consult a licensed professional for anything structural, electrical, plumbing-system, environmental (radon / mold / asbestos), or legal.

## How we limit risk
We tune prompts to prefer "I don't know" over speculation, ground severity claims in the inspector's own language wherever possible, and surface confidence signals where the source narrative is ambiguous. Errors should be reported to support so we can correct them and improve the model.

## Last updated
We refresh this disclosure whenever we add a new AI system, change a model version, or materially adjust how AI affects user-facing outputs.
        `,
    },
    affiliate: {
        title: "Affiliate Disclosure",
        body: `
## FTC compliance
This site may contain affiliate links or sponsored partner relationships in compliance with the U.S. Federal Trade Commission's 16 CFR Part 255 Endorsement Guides.

## What that means
If you click a partner link (for example to request a quote from a contractor network, finance product, or home-services provider) and complete an action on that partner's site, Buyer's Leverage may receive a referral fee. The fee never changes the price you pay.

## How we decide what to recommend
Partner relationships do not influence our severity classifications, repair-cost ranges, or analytical methodology. We disclose all paid relationships in-line wherever a partner link appears, label sponsored content as "Sponsored", and never accept compensation in exchange for omitting a known issue or downgrading a severity.

## How to spot a paid link
Any link on Buyer's Leverage that earns us a referral fee carries one of three signals: (1) the inline phrase "affiliate link", (2) a "Sponsored" pill near the link, or (3) the partner's logo + the explicit disclosure on the destination page. If you ever see a paid link that isn't labeled, please email support@buyersleverage.com so we can fix it.

## Questions
For questions about specific partner relationships, contact support — we'll tell you exactly which partners we work with and what each relationship covers.
        `,
    },
    "professional-disclaimer": {
        title: "Professional Advice Disclaimer",
        body: `
## What Buyer's Leverage is NOT
For the avoidance of doubt, Buyer's Leverage is not, and does not hold itself out as, any of the following:
  • A law firm or a substitute for legal counsel.
  • A licensed home inspector.
  • A structural engineer or any other type of licensed engineer.
  • A general contractor, subcontractor, trade specialist, or estimator.
  • A real estate brokerage, agent, or fiduciary.
  • A financial advisor, mortgage broker, or investment advisor.
  • An insurance provider, broker, or claims adjuster.
  • An appraiser.

## What we ARE
Buyer's Leverage is an educational decision-support platform that helps homebuyers, homeowners, and investors organise inspection-report findings, plan repair-cost exposure, and prepare for negotiation conversations.

## When to seek a professional
You should consult a licensed professional in the relevant jurisdiction before relying on anything on this site for:
  • Legal questions about contracts, contingencies, disclosure obligations, or remedies.
  • Structural or engineering opinions on foundations, framing, load-bearing systems, or grading.
  • Bidding, scoping, or scheduling actual repair work.
  • Decisions to buy, sell, or list real property.
  • Tax, financing, mortgage, or investment-return decisions.
  • Insurance coverage, premium implications, or claim filings.
  • Property valuations or appraisals.

## Language we use
We deliberately use words like "estimated", "potential", "planning-range", "educational", and "informational" to keep expectations honest. We do not guarantee savings, negotiation outcomes, repair costs, or investment returns. Any number, range, or recommendation should be treated as a planning starting point, verified against a licensed professional, and adjusted for your local market.

## Disclaimer is not absolute
This disclaimer does not by itself eliminate any obligation imposed by law. If you have any doubt about whether Buyer's Leverage is the right tool for your situation, consult a licensed professional first.
        `,
    },
    security: {
        title: "Data Security",
        body: `
## Encryption everywhere
All connections between your browser and our servers are TLS 1.2+. Database connections, backup transfers, and admin tooling sessions are encrypted end-to-end.

## Authentication
We use industry-standard password hashing (bcrypt), brute-force protection on the sign-in endpoint, country-of-new-login alerts, optional "Sign in with Google" and "Sign in with Apple" via OAuth, and short-lived session tokens. Admin accounts require multi-factor authentication.

## Role-based access controls
Every API endpoint requires authentication or explicitly allows anonymous access. Admin endpoints require an admin role check at the API layer (not just the UI). Cross-user access is impossible by design — every report and upload row carries an explicit owner_id that the API verifies on every read.

## File storage
Inspection PDFs are stored encrypted at rest on Cloudflare R2. We do not store raw PDFs inside the application database. Backups are AES-256 encrypted, transferred over TLS, and purged on a 90-day rolling window.

## Payments
Stripe processes every payment in compliance with PCI DSS Level 1. We never see, store, or log your card details. Stripe webhooks are signature-verified server-side. Refunds are processed end-to-end via Stripe.

## Audit logging
Every administrative action is appended to an immutable audit log: account deletions, data exports, opt-out toggles, role changes, content deletions. Audit-log entries are tamper-evident and retained for compliance reviews.

## Incident response
We monitor production via uptime probes, error-rate tripwires, smoke tests on critical URLs, and nightly database backup health checks. If we discover a breach affecting your data, we will notify you within 72 hours per GDPR Art. 33 timeline expectations, even outside the EU.

## Vulnerability reporting
If you discover a security vulnerability, email support@buyersleverage.com with a description and reproduction steps. We do not currently run a paid bug bounty, but we credit responsible disclosures publicly with researcher permission.
        `,
    },
    trust: {
        title: "Accessibility & Trust Center",
        body: `
## What this page is
A single hub for every commitment we make about how Buyer's Leverage is built, what it does with your data, what it does NOT claim to do, and how to hold us accountable. Every other policy below is linked from here.

## Accessibility
We are committed to WCAG 2.2 AA and continuously work toward conformance — including external annual audits.
[Read the full Accessibility Statement →](/accessibility)

## Privacy
We do not sell your data. You can download every record we hold on you, delete your account, and opt out of marketing emails or any data "sharing" with one click.
[Read the full Privacy Policy →](/privacy-policy)
[Manage your privacy & data →](/account/privacy)

## AI honesty
We use AI to read inspection reports, classify severity, and assemble negotiation plays. AI can err. We disclose how it's used and what to verify.
[Read the AI Disclosure →](/ai-disclosure)

## What we ARE NOT
We are not a law firm, home inspector, engineer, contractor, brokerage, financial advisor, insurance provider, or appraiser. We help you organise findings — licensed professionals make the calls.
[Read the Professional Advice Disclaimer →](/professional-disclaimer)

## Affiliate transparency
Partner links are labelled. Severity classifications and repair ranges are not influenced by partner relationships.
[Read the Affiliate Disclosure →](/affiliate)

## Security
TLS in transit, encrypted at rest, Stripe for payments, audit logging on every admin action, 72-hour breach-notification commitment.
[Read the Data Security Statement →](/security)

## Terms of service
[Read the Terms →](/terms)

## Cookies
[Read the Cookie Policy →](/cookie-policy)

## Last verified
Each policy carries its own last-updated date. Internally we run an automated WCAG 2.2 AA scan on every deploy and engage an external auditor on a 365-day cadence; deviations are remediated and re-tested before the cycle is recorded as closed.

## How to reach us
For accessibility, privacy, security, or compliance issues: support@buyersleverage.com. We respond within 5 business days.
        `,
    },
};

export default function LegalPage({ pageKey }) {
    const p = PAGES[pageKey] || PAGES.privacy;
    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-16 pb-10">
                    <div className="bl-eyebrow">Legal</div>
                    <h1 className="mt-5 font-display font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] sm:leading-tight text-bl">
                        {p.title}
                    </h1>
                </div>
            </section>
            <section className="bl-container py-12 max-w-3xl" aria-label={p.title}>
                {p.body
                    .trim()
                    .split("\n\n")
                    .map((para, i) => {
                        const trimmed = para.trim();
                        if (trimmed.startsWith("## ")) {
                            const nl = trimmed.indexOf("\n");
                            const heading = (nl > 0 ? trimmed.slice(3, nl) : trimmed.slice(3)).trim();
                            const body = nl > 0 ? trimmed.slice(nl + 1).trim() : "";
                            return (
                                <div key={i}>
                                    <h2
                                        className="mt-10 mb-3 font-display font-semibold text-xl text-bl"
                                        data-testid={`legal-${pageKey}-h2-${i}`}
                                    >
                                        {heading}
                                    </h2>
                                    {body && (
                                        <p
                                            className="text-bl text-[17px] leading-[1.75] mb-5 whitespace-pre-wrap"
                                            data-testid={`legal-${pageKey}-p-${i}`}
                                        >
                                            {renderInline(body)}
                                        </p>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <p
                                key={i}
                                className="text-bl text-[17px] leading-[1.75] mb-5 whitespace-pre-wrap"
                                data-testid={`legal-${pageKey}-p-${i}`}
                            >
                                {renderInline(trimmed)}
                            </p>
                        );
                    })}
            </section>
        </PublicLayout>
    );
}
